import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { resources } from '../../core/services/resources';
import type {
  LineOfBizCategory,
  LineOfBizPersona,
  Prompt,
  PromptFunnelStage,
  PromptImportance,
  TenantBrand,
} from '../../core/types/data-models';
import { TreeEntityMultiSelect } from './TreeEntityMultiSelect';

const IMPORTANCE_OPTIONS: PromptImportance[] = ['LOW', 'MEDIUM', 'HIGH', 'VERY_HIGH'];

/** Single-select picker only ever shows N options — keep the round-trip cheap. */
const PICKER_PAGE_SIZE = 10;
/**
 * Multi-select mode loads up to this many matches so the "All" row can
 * select the full result set in one click. Capped to keep the dropdown and
 * the bulk-attach loop bounded; when more than this match the DB, the
 * picker says so and "All" selects the loaded slice.
 */
const MULTI_PICKER_PAGE_SIZE = 500;
/** Sentinel id for the synthetic "All" row shown at the top in multi mode. */
const ALL_OPTION_ID = -1;

function truncate(text: string, max: number): string {
  if (!text) return '';
  return text.length > max ? `${text.slice(0, max - 1)}…` : text;
}

/** Trivial debounce hook for the free-text search input. */
function useDebounced<T>(value: T, delay = 300): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const handle = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(handle);
  }, [value, delay]);
  return debounced;
}

interface AddPromptPanelProps {
  /** Title shown in the accordion header. Default "Add prompt". */
  title?: string;
  /**
   * Prompt ids already attached to the parent (set / template / etc.) —
   * the picker excludes them client-side so the user can't pick a row
   * the backend would reject.
   */
  excludePromptIds?: Set<number>;
  /**
   * Called when the user clicks the Add button in single-select mode.
   * Receives the full Prompt entity so the parent can choose `id` or
   * `identifier` for the join payload depending on which junction it's
   * attaching to. Required unless `multiple` is set.
   */
  onAdd?: (prompt: Prompt) => Promise<void> | void;
  /**
   * Opt-in multi-select mode: the picker becomes a checkbox multi-select
   * with an "All" row at the top, and the Add button attaches every
   * selected prompt at once via `onAddMany`. Used by the Report-template
   * Prompts tab so admins can batch-attach.
   */
  multiple?: boolean;
  /**
   * Called when the user clicks Add in multi-select mode. Receives every
   * selected prompt. Required when `multiple` is set.
   */
  onAddMany?: (prompts: Prompt[]) => Promise<void> | void;
  /** Label for the add button — e.g. "Add to set" / "Add to template". */
  addButtonLabel?: string;
  /** Error surfaced from the parent's attach mutation. */
  error?: string | null;
  onErrorClose?: () => void;
  /** Whether an attach is in flight (disables the button). */
  isAdding?: boolean;
  /** Whether the accordion opens expanded. Defaults to collapsed. */
  defaultExpanded?: boolean;
  /** Show the Brands filter. Default true (preserves existing consumers). */
  showBrandFilter?: boolean;
  /**
   * Show the Language filter. Options come from `languageOptions`; when
   * that's empty the filter renders disabled. Default false.
   */
  showLanguageFilter?: boolean;
  /** ISO language codes offered in the Language filter. */
  languageOptions?: string[];
  /** Show the Importance filter (LOW / MEDIUM / HIGH / VERY_HIGH). Default false. */
  showImportanceFilter?: boolean;
}

/**
 * Reusable "search the prompt catalog and attach one to a parent" panel.
 *
 * UI: a collapsible accordion with five filter inputs on one row
 * (free-text + Categories tree multi-select + Personas + Stages +
 * Brands tree multi-select) and a result picker that always shows the
 * top 10 matches (server-filtered). Used by Prompt-set and
 * Report-template detail pages — the parent supplies the attach
 * callback so the same UI can write to different junctions.
 */
export function AddPromptPanel({
  title = 'Add prompt',
  excludePromptIds,
  onAdd,
  multiple = false,
  onAddMany,
  addButtonLabel = 'Add',
  error,
  onErrorClose,
  isAdding,
  defaultExpanded = false,
  showBrandFilter = true,
  showLanguageFilter = false,
  languageOptions = [],
  showImportanceFilter = false,
}: AddPromptPanelProps) {
  const [selectedPrompt, setSelectedPrompt] = useState<Prompt | null>(null);
  const [selectedPrompts, setSelectedPrompts] = useState<Prompt[]>([]);
  const [textFilter, setTextFilter] = useState('');
  const debouncedText = useDebounced(textFilter, 300);
  const [categoryFilters, setCategoryFilters] = useState<LineOfBizCategory[]>([]);
  const [personaFilters, setPersonaFilters] = useState<LineOfBizPersona[]>([]);
  const [stageFilters, setStageFilters] = useState<PromptFunnelStage[]>([]);
  const [brandFilters, setBrandFilters] = useState<TenantBrand[]>([]);
  const [languageFilters, setLanguageFilters] = useState<string[]>([]);
  const [importanceFilters, setImportanceFilters] = useState<PromptImportance[]>([]);

  // Flat catalogs for the personas / stages selectors. The brand picker
  // fetches its own catalog via TreeEntityMultiSelect (same cache key).
  // The default-category / default-persona FKs on Prompt now point at
  // the LOB-scoped tables, so the filter lists must use those too.
  const categories = useQuery({
    queryKey: ['line-of-biz-categories', 'select'],
    queryFn: () =>
      resources.lineOfBizCategories.list({ pageSize: 200, sort: 'name' }),
  });
  const personas = useQuery({
    queryKey: ['line-of-biz-personas', 'select'],
    queryFn: () =>
      resources.lineOfBizPersonas.list({ pageSize: 200, sort: 'name' }),
  });
  const stages = useQuery({
    queryKey: ['prompt-funnel-stages', 'select'],
    queryFn: () => resources.promptFunnelStages.list({ pageSize: 200, sort: 'name' }),
  });

  const categoryIds = categoryFilters.map((c) => c.id);
  const personaIds = personaFilters.map((p) => p.id);
  const stageIds = stageFilters.map((s) => s.id);
  const brandIds = brandFilters.map((b) => b.id);

  const promptSearch = useQuery({
    queryKey: [
      'prompts',
      'picker',
      {
        q: debouncedText,
        categoryIds,
        personaIds,
        stageIds,
        brandIds,
        languageFilters,
        importanceFilters,
      },
    ],
    queryFn: () =>
      resources.prompts.list({
        page: 1,
        pageSize: multiple ? MULTI_PICKER_PAGE_SIZE : PICKER_PAGE_SIZE,
        sort: '-id',
        filter: {
          ...(debouncedText.trim() ? { q: debouncedText.trim() } : {}),
          ...(categoryIds.length ? { defaultCategoryId: categoryIds } : {}),
          ...(personaIds.length ? { defaultPersonaId: personaIds } : {}),
          ...(stageIds.length ? { defaultStageId: stageIds } : {}),
          ...(brandIds.length ? { defaultBrandId: brandIds } : {}),
          ...(languageFilters.length ? { language: languageFilters } : {}),
          ...(importanceFilters.length ? { defaultImportance: importanceFilters } : {}),
        },
      }),
    placeholderData: (prev) => prev,
  });

  const pickerOptions = (promptSearch.data?.data ?? []).filter(
    (p) => !excludePromptIds?.has(p.id),
  );

  // Total rows matching the current filters in the database — NOT the
  // count loaded into the dropdown. This is what the "X match the filters"
  // figure reports, so the user sees the true catalog-wide match count
  // even though the picker only loads a bounded slice.
  const dbMatchCount = promptSearch.data?.page.total ?? 0;
  // How many rows the picker actually pulled this round (pre-exclude) — used
  // to tell the user when the DB has more matches than were loaded.
  const loadedCount = promptSearch.data?.data.length ?? 0;
  const allLoadedSelected =
    pickerOptions.length > 0 && selectedPrompts.length === pickerOptions.length;
  const someSelected = selectedPrompts.length > 0 && !allLoadedSelected;

  // Drop any staged selections whenever the filter set changes — the loaded
  // option list shifts underneath us, so keeping stale picks (and a stale
  // "All") would be misleading.
  const filterSig = JSON.stringify({
    q: debouncedText,
    categoryIds,
    personaIds,
    stageIds,
    brandIds,
    languageFilters,
    importanceFilters,
  });
  useEffect(() => {
    setSelectedPrompts([]);
  }, [filterSig]);

  const handleAdd = async () => {
    if (!selectedPrompt) return;
    await onAdd?.(selectedPrompt);
    // Reset the selection on success so the user can add another. The
    // parent is in charge of refreshing exclude ids; if the attach
    // throws, it owns surfacing the error via the `error` prop.
    setSelectedPrompt(null);
  };

  const handleAddMany = async () => {
    if (selectedPrompts.length === 0) return;
    try {
      await onAddMany?.(selectedPrompts);
      // Clear on success only. If the bulk attach throws — or the parent
      // opens a params dialog and the user cancels it (rejecting the
      // returned promise) — the selection stays so the user can retry.
      setSelectedPrompts([]);
    } catch {
      // Swallow: the parent owns surfacing any error via the `error` prop.
    }
  };

  // Render a compact multi-select Autocomplete — labelled, chip-collapsed,
  // and shrunk to fit five filters on one row.
  const filterAutocomplete = <T extends { id: number; name?: string }>(args: {
    options: T[];
    value: T[];
    onChange: (next: T[]) => void;
    label: string;
    loading?: boolean;
  }) => (
    <Autocomplete
      multiple
      sx={{ flex: 1, minWidth: 160 }}
      options={args.options}
      value={args.value}
      onChange={(_, next) => args.onChange(next)}
      getOptionLabel={(o) => o.name ?? `#${o.id}`}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      loading={args.loading}
      limitTags={1}
      size="small"
      renderInput={(params) => <TextField {...params} label={args.label} placeholder="Any" />}
    />
  );

  return (
    <Accordion
      disableGutters
      defaultExpanded={defaultExpanded}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        '&:before': { display: 'none' },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls="add-prompt-panel"
        id="add-prompt-header"
      >
        <Typography variant="subtitle1" fontWeight={600}>
          {title}
        </Typography>
      </AccordionSummary>
      <AccordionDetails id="add-prompt-panel">
        {/* Filter cluster: text + multi-selects. Which selects appear is
            consumer-driven (Brands / Language / Importance are opt-in), so
            the row wraps to a second line when the set is wide. */}
        <Stack
          direction="row"
          spacing={1.5}
          useFlexGap
          flexWrap="wrap"
          sx={{ mb: 2, rowGap: 1.5 }}
        >
          <TextField
            size="small"
            sx={{ flex: 1.4, minWidth: 200 }}
            label="Prompt text contains…"
            value={textFilter}
            onChange={(e) => setTextFilter(e.target.value)}
            placeholder="Free-text search"
          />
          {filterAutocomplete({
            options: categories.data?.data ?? [],
            value: categoryFilters,
            onChange: setCategoryFilters,
            label: 'Categories',
            loading: categories.isLoading,
          })}
          {filterAutocomplete({
            options: personas.data?.data ?? [],
            value: personaFilters,
            onChange: setPersonaFilters,
            label: 'Personas',
            loading: personas.isLoading,
          })}
          {filterAutocomplete({
            options: stages.data?.data ?? [],
            value: stageFilters,
            onChange: setStageFilters,
            label: 'Stages',
            loading: stages.isLoading,
          })}
          {showBrandFilter && (
            <Box sx={{ flex: 1, minWidth: 160 }}>
              <TreeEntityMultiSelect<TenantBrand>
                cacheKey={['tenant-brands', 'select', 'all']}
                service={resources.tenantBrands}
                parentIdField="parentBrandId"
                value={brandFilters}
                onChange={setBrandFilters}
                label="Brands"
                placeholder="Any"
                size="small"
                limitTags={1}
              />
            </Box>
          )}
          {showLanguageFilter && (
            <Autocomplete<string, true, false, false>
              multiple
              sx={{ flex: 1, minWidth: 160 }}
              options={languageOptions}
              value={languageFilters}
              onChange={(_, next) => setLanguageFilters(next)}
              getOptionLabel={(code) => code.toUpperCase()}
              isOptionEqualToValue={(a, b) => a === b}
              limitTags={1}
              size="small"
              renderInput={(params) => (
                <TextField {...params} label="Languages" placeholder="Any" />
              )}
            />
          )}
          {showImportanceFilter && (
            <Autocomplete<PromptImportance, true, false, false>
              multiple
              sx={{ flex: 1, minWidth: 160 }}
              options={IMPORTANCE_OPTIONS}
              value={importanceFilters}
              onChange={(_, next) => setImportanceFilters(next)}
              isOptionEqualToValue={(a, b) => a === b}
              limitTags={1}
              size="small"
              renderInput={(params) => (
                <TextField {...params} label="Importance" placeholder="Any" />
              )}
            />
          )}
        </Stack>

        {/* Picker. Single mode: one prompt + Add. Multi mode: checkbox
            multi-select with an "All" row, a live "selected / matched in
            DB" count, and a batch Add. */}
        <Stack direction="row" spacing={2} alignItems="flex-start">
          {multiple ? (
            <Autocomplete
              multiple
              disableCloseOnSelect
              sx={{ flex: 1, minWidth: 320 }}
              // Prepend the synthetic "All" row so it renders first.
              options={[{ id: ALL_OPTION_ID } as Prompt, ...pickerOptions]}
              value={selectedPrompts}
              onChange={(_, next) => {
                // The "All" row toggles select-all of the loaded matches:
                // clicking it adds the sentinel to `next`, which we treat
                // as "select everything loaded" (or clear, if already all).
                if (next.some((o) => o.id === ALL_OPTION_ID)) {
                  setSelectedPrompts(allLoadedSelected ? [] : pickerOptions);
                } else {
                  setSelectedPrompts(next.filter((o) => o.id !== ALL_OPTION_ID));
                }
              }}
              getOptionLabel={(p) => (p.id === ALL_OPTION_ID ? 'All' : p.prompt)}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              loading={promptSearch.isFetching}
              limitTags={2}
              renderTags={(value) => (
                <Typography variant="body2" sx={{ pl: 1 }}>
                  {value.length} selected
                </Typography>
              )}
              renderOption={(props, option, { selected }) => {
                if (option.id === ALL_OPTION_ID) {
                  return (
                    <Box component="li" {...props} key="__all">
                      <Checkbox
                        size="small"
                        sx={{ mr: 1 }}
                        checked={allLoadedSelected}
                        indeterminate={someSelected}
                      />
                      <Typography variant="body2" fontWeight={600}>
                        All{dbMatchCount ? ` (${dbMatchCount})` : ''}
                      </Typography>
                    </Box>
                  );
                }
                return (
                  <Box component="li" {...props} key={option.id}>
                    <Checkbox size="small" sx={{ mr: 1 }} checked={selected} />
                    <Typography variant="body2">{truncate(option.prompt, 140)}</Typography>
                  </Box>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Pick a prompt"
                  helperText={
                    promptSearch.isFetching
                      ? 'Searching…'
                      : dbMatchCount === 0
                        ? 'No prompts match the current filters.'
                        : `${selectedPrompts.length} selected · ${dbMatchCount} match the filters${
                            dbMatchCount > loadedCount
                              ? ` (first ${loadedCount} loaded — narrow the filters to reach the rest)`
                              : ''
                          }`
                  }
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {promptSearch.isFetching ? <CircularProgress size={16} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          ) : (
            <Autocomplete
              sx={{ flex: 1, minWidth: 320 }}
              options={pickerOptions}
              value={selectedPrompt}
              onChange={(_, next) => setSelectedPrompt(next)}
              getOptionLabel={(p) => p.prompt}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              loading={promptSearch.isFetching}
              renderOption={(props, option) => (
                <Box component="li" {...props} key={option.id}>
                  <Typography variant="body2">{truncate(option.prompt, 140)}</Typography>
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Pick a prompt"
                  helperText={
                    pickerOptions.length === 0 && !promptSearch.isFetching
                      ? 'No prompts match the current filters.'
                      : `Showing first ${pickerOptions.length} match${pickerOptions.length === 1 ? '' : 'es'} (of up to ${PICKER_PAGE_SIZE}). Narrow the filters to find more.`
                  }
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {promptSearch.isFetching ? <CircularProgress size={16} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          )}
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            disabled={
              (multiple ? selectedPrompts.length === 0 : !selectedPrompt) || !!isAdding
            }
            onClick={multiple ? handleAddMany : handleAdd}
            sx={{ mt: 1 }}
          >
            {isAdding
              ? 'Adding…'
              : multiple && selectedPrompts.length > 0
                ? `${addButtonLabel} (${selectedPrompts.length})`
                : addButtonLabel}
          </Button>
        </Stack>
        {error && (
          <Alert severity="error" sx={{ mt: 2 }} onClose={onErrorClose}>
            {error}
          </Alert>
        )}
      </AccordionDetails>
    </Accordion>
  );
}
